import React, { useState, useRef } from 'react';
import { COMMON_STYLES } from '../utils/constants';

/**
 * Composant d'import CSV/TXT pour clients et lieux de mise
 * Formats supportés :
 * - CSV : séparateur point-virgule (;)
 * - TXT : séparateur tabulation (\t) - format VIF
 * Colonnes attendues : Client, Libellé, Nature, Adresse 1, Code postal, Ville, Téléphone
 * Nature = "Client" ou "Acteur" (lieu de mise)
 */

// Styles locaux spécifiques au composant
const STYLES = {
  modalContent: {
    ...COMMON_STYLES.MODAL_CONTENT,
    padding: '30px',
    width: '100%',
    maxWidth: '750px',
    maxHeight: '90vh',
    overflow: 'auto'
  },
  stepContainer: {
    ...COMMON_STYLES.FLEX_ROW_BETWEEN,
    marginBottom: '25px',
    padding: '0 10px'
  },
  stepItem: {
    ...COMMON_STYLES.FLEX_COLUMN_CENTER,
    flex: 1
  },
  infoBox: {
    background: '#e3f2fd',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px'
  },
  fileButton: {
    padding: '12px 24px',
    background: '#f8f9fa',
    border: '2px dashed #dee2e6',
    borderRadius: '8px',
    cursor: 'pointer',
    width: '100%',
    fontSize: '14px'
  },
  previewTable: {
    ...COMMON_STYLES.TABLE,
    fontSize: '12px'
  },
  tableHeader: {
    padding: '8px',
    border: '1px solid #dee2e6',
    textAlign: 'left',
    background: '#f8f9fa'
  },
  tableCell: {
    padding: '8px',
    border: '1px solid #dee2e6'
  },
  actionButton: {
    flex: 1,
    padding: '12px',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontWeight: '600'
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    border: '2px solid #e1e5e9',
    borderRadius: '8px',
    background: 'white',
    cursor: 'pointer',
    fontWeight: '600'
  }
};

const CSVImport = ({ token, onClose, onImportComplete, importType = 'clients', apiEndpoint }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [preview, setPreview] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [step, setStep] = useState('select'); // select, preview, analyze, import, done
  const [fileFormat, setFileFormat] = useState(null); // 'csv' ou 'txt'
  const [forceUpdate, setForceUpdate] = useState(false); // Forcer la MAJ des existants
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' }); // Progression import
  const fileInputRef = useRef(null);

  /**
   * Détecte le séparateur selon l'extension du fichier
   */
  const getDelimiter = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    if (ext === 'txt') return '\t'; // Tabulation pour fichiers TXT (format VIF)
    return ';'; // Point-virgule pour fichiers CSV
  };

  const parseFile = (text, delimiter) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('Le fichier doit contenir au moins une ligne de données');
    }

    // Chercher la ligne d'en-têtes (peut ne pas être la première ligne dans VIF)
    const expectedHeaders = ['Client', 'Libellé', 'Nature', 'Adresse 1', 'Code postal', 'Ville', 'Téléphone'];
    let headerLineIndex = -1;
    let headerIndexes = {};

    for (let i = 0; i < Math.min(lines.length, 20); i++) {
      const headers = lines[i].split(delimiter).map(h => h.trim());

      // Vérifier si cette ligne contient les en-têtes attendus
      const foundHeaders = expectedHeaders.filter(expected =>
        headers.some(h => h.toLowerCase() === expected.toLowerCase())
      );

      if (foundHeaders.length >= 5) { // Au moins 5 colonnes reconnues
        headerLineIndex = i;
        // Mapper les index des colonnes
        expectedHeaders.forEach(expected => {
          const idx = headers.findIndex(h => h.toLowerCase() === expected.toLowerCase());
          if (idx !== -1) {
            headerIndexes[expected] = idx;
          }
        });
        break;
      }
    }

    if (headerLineIndex === -1) {
      throw new Error(`Format de fichier invalide. En-têtes attendus: ${expectedHeaders.join(', ')}`);
    }

    // Parser les lignes de données (après la ligne d'en-têtes)
    const data = [];
    for (let i = headerLineIndex + 1; i < lines.length; i++) {
      const values = lines[i].split(delimiter).map(v => v.trim());

      // Récupérer les valeurs selon les index des colonnes
      const code = values[headerIndexes['Client']] || '';
      const name = values[headerIndexes['Libellé']] || '';
      const nature = values[headerIndexes['Nature']] || '';
      const address = values[headerIndexes['Adresse 1']] || '';
      const postalCode = values[headerIndexes['Code postal']] || '';
      const city = values[headerIndexes['Ville']] || '';
      const phone = values[headerIndexes['Téléphone']] || '';

      // Ignorer les lignes vides ou sans code
      if (!code || !name) continue;

      // Filtrer selon le type d'import
      if (importType === 'clients' && nature !== 'Client') continue;
      if (importType === 'locations' && nature !== 'Acteur') continue;

      data.push({
        code,
        name,
        nature,
        address,
        postal_code: postalCode,
        city,
        phone
      });
    }

    return data;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Vérifier l'extension
    const ext = selectedFile.name.toLowerCase().split('.').pop();
    if (!['csv', 'txt'].includes(ext)) {
      setError('Format non supporté. Utilisez un fichier .csv ou .txt');
      return;
    }

    setFile(selectedFile);
    setFileFormat(ext);
    setError('');
    setResults(null);
    setAnalysis(null);
    setStep('select');

    const delimiter = getDelimiter(selectedFile.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const data = parseFile(text, delimiter);

        if (data.length === 0) {
          setError(`Aucun enregistrement de type "${importType === 'clients' ? 'Client' : 'Acteur'}" trouvé dans le fichier`);
          setPreview([]);
          setParsedData([]);
          setTotalCount(0);
          return;
        }

        setParsedData(data);
        setTotalCount(data.length);
        setPreview(data.slice(0, 10)); // Aperçu des 10 premiers
        setStep('preview');
        setError('');
      } catch (err) {
        setError(err.message);
        setPreview([]);
        setParsedData([]);
      }
    };
    reader.onerror = () => {
      setError('Erreur lors de la lecture du fichier');
    };
    // Essayer Windows-1252 (encodage VIF) puis UTF-8
    reader.readAsText(selectedFile, 'windows-1252');
  };

  const handleAnalyze = async () => {
    if (parsedData.length === 0) return;

    setAnalyzing(true);
    setError('');

    try {
      const analyzeEndpoint = apiEndpoint.replace('/import', '/analyze');
      const response = await fetch(analyzeEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          [importType]: parsedData
        })
      });

      const result = await response.json();

      if (result.success) {
        setAnalysis(result.data);
        setStep('analyze');
      } else {
        setError(result.error || 'Erreur lors de l\'analyse');
      }
    } catch (err) {
      setError('Erreur lors de l\'analyse: ' + err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleImport = async () => {
    if (parsedData.length === 0) return;

    setImporting(true);
    setError('');
    setStep('import');
    setProgress({ current: 0, total: parsedData.length, message: 'Démarrage...' });

    try {
      // Ajouter ?forceUpdate=true si l'option est activée
      const url = forceUpdate ? `${apiEndpoint}?forceUpdate=true` : apiEndpoint;

      // Utiliser fetch avec SSE pour la progression
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          [importType]: parsedData
        })
      });

      // Lire le stream SSE
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let completed = false;

      // Fonction pour traiter les messages SSE
      const processSSEData = (dataStr) => {
        try {
          const data = JSON.parse(dataStr);

          if (data.type === 'progress') {
            setProgress({ current: data.current, total: data.total, message: data.message });
          } else if (data.type === 'complete') {
            setResults(data.data);
            setStep('done');
            setImporting(false);
            completed = true;
            // Ne PAS appeler onImportComplete ici - sera appelé à la fermeture
            return true; // Signal pour arrêter
          } else if (data.type === 'error') {
            setError(data.error || 'Erreur lors de l\'import');
            setStep('analyze');
            completed = true;
            return true; // Signal pour arrêter
          }
        } catch (parseErr) {
          console.warn('Erreur parsing SSE:', parseErr);
        }
        return false;
      };

      while (!completed) {
        const { done, value } = await reader.read();

        if (value) {
          buffer += decoder.decode(value, { stream: !done });
        }

        // Traiter les lignes complètes
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || ''; // Garder le dernier fragment incomplet

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            if (processSSEData(line.slice(6))) {
              break;
            }
          }
        }

        // Si le stream est terminé et qu'on n'a pas encore trouvé complete
        if (done && !completed) {
          // Traiter le buffer restant
          if (buffer.startsWith('data: ')) {
            processSSEData(buffer.slice(6));
          }
          break;
        }
      }
    } catch (err) {
      setError('Erreur lors de l\'import: ' + err.message);
      setStep('analyze');
    } finally {
      setImporting(false);
    }
  };

  const title = importType === 'clients' ? 'Import des clients' : 'Import des lieux de mise';
  const filterType = importType === 'clients' ? 'Client' : 'Acteur';

  // Rendu des étapes
  const renderSteps = () => {
    const steps = ['Fichier', 'Aperçu', 'Analyse', 'Import'];
    const stepNames = ['select', 'preview', 'analyze', 'done'];
    const currentIdx = stepNames.indexOf(step === 'import' ? 'analyze' : step);

    return (
      <div style={STYLES.stepContainer}>
        {steps.map((label, idx) => {
          const isActive = idx <= currentIdx;
          const isCurrent = idx === currentIdx;
          return (
            <div key={label} style={STYLES.stepItem}>
              <div style={{
                ...COMMON_STYLES.STEP_CIRCLE,
                background: isActive ? (isCurrent ? '#007bff' : '#28a745') : '#e9ecef',
                color: isActive ? 'white' : '#6c757d'
              }}>
                {idx < currentIdx ? '✓' : idx + 1}
              </div>
              <span style={{
                ...COMMON_STYLES.STEP_LABEL,
                color: isActive ? '#212529' : '#6c757d'
              }}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Rendu stat card
  const StatCard = ({ value, label, bgColor, textColor }) => (
    <div style={{ ...COMMON_STYLES.STAT_CARD, background: bgColor }}>
      <div style={{ ...COMMON_STYLES.STAT_VALUE, color: textColor }}>{value}</div>
      <div style={{ ...COMMON_STYLES.STAT_LABEL, color: textColor }}>{label}</div>
    </div>
  );

  return (
    <div style={COMMON_STYLES.MODAL_OVERLAY}>
      <div style={STYLES.modalContent}>
        <div style={{ ...COMMON_STYLES.FLEX_ROW_BETWEEN, marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={COMMON_STYLES.CLOSE_BUTTON}>✕</button>
        </div>

        {renderSteps()}

        {/* Instructions */}
        {step === 'select' && (
          <div style={STYLES.infoBox}>
            <strong>Formats acceptés :</strong>
            <br />
            <span style={{ fontSize: '12px' }}>
              • <strong>CSV</strong> (séparateur ;) : <code>Client;Libelle;Nature;Adresse 1;Code postal;Ville;Telephone</code>
              <br />
              • <strong>TXT</strong> (séparateur tabulation) : export VIF (acteurs.txt, CLIENTS.txt)
            </span>
            <br /><br />
            <strong>Règles d'import :</strong>
            <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
              <li>Seuls les enregistrements avec Nature = "<strong>{filterType}</strong>" seront importes</li>
              <li>Les codes existants seront ignores (pas d'ecrasement)</li>
              <li>Les nouveaux codes seront crees</li>
            </ul>
          </div>
        )}

        {/* Sélecteur de fichier */}
        {(step === 'select' || step === 'preview') && (
          <div style={{ marginBottom: '20px' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <button onClick={() => fileInputRef.current.click()} style={STYLES.fileButton}>
              {file ? `Fichier: ${file.name}` : 'Cliquer pour sélectionner un fichier CSV ou TXT'}
            </button>
          </div>
        )}

        {/* Erreur */}
        {error && (
          <div style={{ ...COMMON_STYLES.ALERT, ...COMMON_STYLES.ALERT_DANGER }}>
            {error}
          </div>
        )}

        {/* Aperçu */}
        {step === 'preview' && preview.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 10px 0' }}>
              Aperçu ({preview.length} sur {totalCount} enregistrements)
            </h4>
            <div style={{ overflowX: 'auto', maxHeight: '200px' }}>
              <table style={STYLES.previewTable}>
                <thead>
                  <tr style={{ position: 'sticky', top: 0 }}>
                    <th style={STYLES.tableHeader}>Code</th>
                    <th style={STYLES.tableHeader}>Nom</th>
                    <th style={STYLES.tableHeader}>Ville</th>
                    <th style={STYLES.tableHeader}>Tel</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((item, idx) => (
                    <tr key={idx}>
                      <td style={STYLES.tableCell}>{item.code}</td>
                      <td style={STYLES.tableCell}>{item.name}</td>
                      <td style={STYLES.tableCell}>{item.city}</td>
                      <td style={STYLES.tableCell}>{item.phone}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Analyse */}
        {step === 'analyze' && analysis && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 15px 0' }}>Analyse du fichier</h4>

            <div style={{ ...COMMON_STYLES.GRID_3, marginBottom: '20px' }}>
              <StatCard value={analysis.total} label="Total dans le fichier" bgColor="#f8f9fa" textColor="#495057" />
              <StatCard value={analysis.toCreate} label="A créer (nouveaux)" bgColor="#d4edda" textColor="#155724" />
              <StatCard value={analysis.existing} label="Existants (ignorés)" bgColor="#fff3cd" textColor="#856404" />
            </div>

            {analysis.existing > 0 && (
              <div style={{
                background: forceUpdate ? '#e3f2fd' : '#fff3cd',
                padding: '15px',
                borderRadius: '8px',
                marginTop: '15px'
              }}>
                <label style={{ ...COMMON_STYLES.FLEX_ROW, gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={forceUpdate}
                    onChange={(e) => setForceUpdate(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ color: forceUpdate ? '#0d47a1' : '#856404' }}>
                    <strong>Forcer la mise à jour</strong> des {analysis.existing} fiche(s) existante(s)
                    <br />
                    <span style={{ fontSize: '12px' }}>
                      (Utile pour corriger les données tronquées ou incomplètes)
                    </span>
                  </span>
                </label>
              </div>
            )}

            {analysis.toCreate === 0 && !forceUpdate && (
              <div style={{ ...COMMON_STYLES.ALERT, ...COMMON_STYLES.ALERT_WARNING, marginTop: '15px' }}>
                <strong>Attention :</strong> Tous les enregistrements existent déjà en base. Cochez "Forcer la mise à jour" pour les mettre à jour.
              </div>
            )}
          </div>
        )}

        {/* Import en cours avec barre de progression */}
        {step === 'import' && (
          <div style={{ ...COMMON_STYLES.EMPTY_STATE, padding: '40px 20px', marginBottom: '20px' }}>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#495057', marginBottom: '15px' }}>
              Import en cours...
            </div>

            <div style={COMMON_STYLES.PROGRESS_CONTAINER}>
              <div style={{
                ...COMMON_STYLES.PROGRESS_BAR,
                width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%`
              }} />
            </div>

            <div style={{ fontSize: '16px', fontWeight: '600', color: '#007bff' }}>
              {progress.current} / {progress.total}
            </div>
            <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '5px' }}>
              {progress.message || 'Traitement en cours...'}
            </div>
            <div style={{ fontSize: '12px', color: '#adb5bd', marginTop: '10px' }}>
              {progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}% terminé
            </div>
          </div>
        )}

        {/* Résultats */}
        {step === 'done' && results && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ ...COMMON_STYLES.ALERT, ...COMMON_STYLES.ALERT_SUCCESS, flexDirection: 'column', alignItems: 'stretch', padding: '20px' }}>
              <h4 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '24px' }}>✓</span>
                Import terminé avec succès !
              </h4>

              <div style={results.updated > 0 ? COMMON_STYLES.GRID_4 : COMMON_STYLES.GRID_3}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#155724' }}>{results.created}</div>
                  <div style={{ fontSize: '12px', color: '#155724' }}>Créé(s)</div>
                </div>
                {results.updated > 0 && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#0d47a1' }}>{results.updated}</div>
                    <div style={{ fontSize: '12px', color: '#0d47a1' }}>Mis à jour</div>
                  </div>
                )}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#856404' }}>{results.skipped}</div>
                  <div style={{ fontSize: '12px', color: '#856404' }}>Ignoré(s)</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: results.errors?.length > 0 ? '#721c24' : '#6c757d' }}>
                    {results.errors?.length || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: results.errors?.length > 0 ? '#721c24' : '#6c757d' }}>Erreur(s)</div>
                </div>
              </div>
            </div>

            {results.errors?.length > 0 && (
              <div style={{
                ...COMMON_STYLES.ALERT,
                ...COMMON_STYLES.ALERT_DANGER,
                flexDirection: 'column',
                alignItems: 'stretch',
                maxHeight: '150px',
                overflow: 'auto'
              }}>
                <strong>Erreurs :</strong>
                <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px', fontSize: '13px' }}>
                  {results.errors.slice(0, 10).map((err, idx) => (
                    <li key={idx}>{err.code} - {err.name}: {err.error}</li>
                  ))}
                  {results.errors.length > 10 && (
                    <li>... et {results.errors.length - 10} autres erreurs</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Boutons */}
        <div style={{ ...COMMON_STYLES.FLEX_ROW, gap: '10px' }}>
          <button
            onClick={() => {
              if (step === 'done' && onImportComplete) {
                onImportComplete(results);
              }
              onClose();
            }}
            style={STYLES.cancelButton}
          >
            {step === 'done' ? 'Fermer' : 'Annuler'}
          </button>

          {step === 'preview' && (
            <button
              onClick={handleAnalyze}
              disabled={analyzing || totalCount === 0}
              style={{
                ...STYLES.actionButton,
                background: totalCount > 0 ? 'linear-gradient(135deg, #007bff, #0056b3)' : '#ccc',
                cursor: totalCount > 0 && !analyzing ? 'pointer' : 'not-allowed'
              }}
            >
              {analyzing ? 'Analyse en cours...' : `Analyser ${totalCount} enregistrement(s)`}
            </button>
          )}

          {step === 'analyze' && analysis && (
            <button
              onClick={handleImport}
              disabled={importing || (analysis.toCreate === 0 && !forceUpdate)}
              style={{
                ...STYLES.actionButton,
                background: (analysis.toCreate > 0 || forceUpdate) ? 'linear-gradient(135deg, #28a745, #218838)' : '#ccc',
                cursor: (analysis.toCreate > 0 || forceUpdate) && !importing ? 'pointer' : 'not-allowed'
              }}
            >
              {importing ? 'Import en cours...' : (
                forceUpdate
                  ? `Mettre à jour ${analysis.existing} + créer ${analysis.toCreate}`
                  : `Importer ${analysis.toCreate} nouveau(x)`
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CSVImport;
